import { useState, useEffect, useCallback, useRef } from 'react';
import { usePost } from '../../../API';
import type * as survey from '@aidkitorg/types/lib/survey';
import type { SupportedLanguage } from '@aidkitorg/i18n/lib';

interface UseDashboardDownloadOptions {
  filename: string;
  downloadPath: string;
  asyncDownload?: boolean;
  dashboard: string | survey.Dashboard;
  lang?: SupportedLanguage;
}

/**
 * Shared hook for dashboard CSV downloads (sync + async/polling).
 * Used by PrecomputedChartCard and DownloadOnlyCard.
 */
export function useDashboardDownload(options: UseDashboardDownloadOptions | null) {
  const downloadData = usePost('/dashboard/download', { textOnly: true });
  const fetchAsyncDownload = usePost('/dashboard/fetch_async_download');
  const [downloadInProgress, setDownloadInProgress] = useState(false);
  const [taskId, setTaskId] = useState<string | null>(null);

  // Stabilize options so polling doesn't restart on every render
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const handleDownload = useCallback(async () => {
    const opts = optionsRef.current;
    if (!opts) return;
    const { filename, downloadPath, asyncDownload, dashboard, lang } = opts;
    try {
      setDownloadInProgress(true);
      if (asyncDownload) {
        const result = await downloadData({
          dashboard, filterIndexes: {}, format: 'csv',
          downloadPath, asyncDownload: true, lang,
        });
        const parsed = typeof result === 'string' ? JSON.parse(result) : result;
        setTaskId(parsed.taskId);
      } else {
        const csvData = await downloadData({
          dashboard, filterIndexes: {}, format: 'csv',
          downloadPath, lang,
        });
        const blob = new Blob([csvData as any], { type: 'text/csv' });
        const elem = document.createElement('a');
        elem.href = URL.createObjectURL(blob);
        elem.download = `${filename}.csv`;
        document.body.appendChild(elem);
        elem.click();
        document.body.removeChild(elem);
        setDownloadInProgress(false);
      }
    } catch {
      setDownloadInProgress(false);
    }
  }, [downloadData]);

  // Poll for async download
  const pollForDownload = useCallback(async () => {
    const opts = optionsRef.current;
    if (!taskId || !opts) return;
    const resp = await fetchAsyncDownload({ taskId });
    if (resp.url) {
      const link = document.createElement('a');
      link.href = resp.url;
      link.setAttribute('download', `${opts.filename}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTaskId(null);
      setDownloadInProgress(false);
    } else if (resp.status === 'FAILED' || resp.error) {
      setTaskId(null);
      setDownloadInProgress(false);
    }
  }, [taskId, fetchAsyncDownload]);

  useEffect(() => {
    if (taskId) {
      const intervalId = setInterval(pollForDownload, 1000);
      return () => clearInterval(intervalId);
    }
  }, [taskId, pollForDownload]);

  return { downloadInProgress, handleDownload };
}
